import {
    ECJuridicalType,
    ECLandRegistryData,
    ECLegalForm,
    ECMember,
    ECMemberPOD,
    ECMemberPropertyUnitWithRelations,
    ECMemberRelation,
    ECMemberRelationTargetType,
    ECPodCreationType,
    ECSubjectType,
    EnergyCommunityNamedPod,
    ENTITY_METADATA,
    EntityType,
    Pod,
    PodStatisticalProfile,
    SpatialCoordinates
} from 'smag-commons/cfg/hierarchy';
import {ConfigurableField, DeepFieldConfig, FieldConfig} from 'smag-commons/dto/api';
import {AbstractControl, FormArray, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators} from '@angular/forms';
import {ECMemberIdRelation} from '../../../shared/ec-members';
import {errorCode, isBlank} from '../../../commons/commons';
import {ErrorCode} from '../../../api/errors';
import {
    ECMemberAddress,
    ECMemberDocument,
    ECMemberPropertyUnit,
    ECMemberRelationType,
    EnergyCommunityNamedPlant
} from 'smag-commons/cfg/hierarchy/ec';
import * as _ from 'lodash';
import {isDefined} from 'smag-commons/fcn';
import {FormError, getEntitySubtype, getPodEnergyProfile, mkTextField, PodEnergyProfiles} from '../../../shared/energy-community';
import {validatePod as smagValidatePod} from 'smag-commons/fcn/validators';
import {EditedDocument, MemberForConfig, MemberRange, PodInfo, PropertyUnitFormError, PropertyUnitTriplet} from './new-member.model';
import * as moment from 'moment-timezone';
import {Moment} from 'moment-timezone';
import {UPPER_INCENTIVE_BOUNDARY} from '../../../shared/ec-economics';
import {PredictiveModelInfo} from 'smag-commons/cfg/prediction';
import {I18NService} from '../../../i18n/i18n.service';
import {ECEconomicsRedistributedNewCondoRow} from 'smag-commons/dto/eco/community';

function mkMemberPodRelation(pod: string, startDate: Moment, endDate: Moment): ECMemberRelation {
    return {
        target_id: pod,
        target_type: ECMemberRelationTargetType.POD,
        type: ECMemberRelationType.HOLDER,
        start_date: startDate ? startDate.valueOf() : null,
        end_date: endDate ? endDate.valueOf() : null
    };
}

function mkPropertyUnitRelation(id: string, startDate: Moment, endDate: Moment): ECMemberRelation {
    return {
        target_id: id, // temporary id or already set existing id
        type: ECMemberRelationType.OWNER, // it must always be owner
        target_type: ECMemberRelationTargetType.PROPERTY_UNIT,
        start_date: startDate ? startDate.valueOf() : null,
        end_date: endDate ? endDate.valueOf() : null
    };
}

export function mkErrorMessage(err: Error, fallback: string): string {
    const code = errorCode(err);
    switch (code) {
        case ErrorCode.POD_CONNECTED_TO_NEW_ENTITY_NOT_IN_COMMUNITY:
        case ErrorCode.POD_CONNECTED_TO_ENTITY_NOT_IN_COMMUNITY:
            return 'POD connects resource to a member that is not part of this Energy Community';
        case ErrorCode.MEMBER_EMAIL_ALREADY_EXISTING:
            return 'A subject with this email already exists';
        case ErrorCode.MEMBER_EMAIL_ALREADY_EXISTING_FOR_USER:
            return 'A user with this email already exists';
        case ErrorCode.MEMBER_MOBILE_ALREADY_EXISTING:
            return 'A subject with this mobile number already exists';
        case ErrorCode.MEMBER_MOBILE_ALREADY_EXISTING_FOR_USER:
            return 'A user with this mobile number already exists';
        case ErrorCode.MISSING_REQUIRED_VALUES:
            return 'A required value is missing';
        case ErrorCode.INVALID_POD_ACTIVATION_DATE:
            return 'Invalid POD activation date';
        case ErrorCode.INVALID_FILE_TYPE:
            return 'Invalid file type';
        case ErrorCode.INVALID_MEMBER_JURIDICAL_TYPE:
            return 'Invalid energy community member juridical type provided';
        case ErrorCode.INVALID_POD_UTILITY_TYPE:
            return 'Invalid POD utility type provided';
        case ErrorCode.INVALID_DATE:
            return 'Invalid date provided';
        case ErrorCode.INCONSISTENT_POD_SUBJECT_RELATIONS:
            return 'This POD has inconsistent relations with a subject';
        default:
            return fallback;
    }
}

export function isSameDocument(editedDoc: EditedDocument, doc: ECMemberDocument): boolean {
    return !editedDoc.file &&
        editedDoc.documentType === doc.document_type &&
        _.isEqual(editedDoc.metadata || {}, doc.metadata || {}) &&
        sameExpiration(editedDoc.expiration, doc.expire_date);
}

function sameExpiration(exp1: number, exp2: number): boolean {
    return (isDefined(exp1) ? exp1 : null) === (isDefined(exp2) ? exp2 : null);
}

function mkPodLandRegistryData(podForm: FormGroup, podAndLrdMap: Map<string, ECLandRegistryData>): ECLandRegistryData | null {
    // forbidden ($$...$$) land registry data must not be shown, but they must not be lost
    const pod = podForm.controls.podCode.value;
    let section = mkTextField(podForm.controls[ConfigurableField.LAND_REGISTRY_SECTION]?.value);
    let sheet = mkTextField(podForm.controls[ConfigurableField.LAND_REGISTRY_SHEET]?.value);
    let particle = mkTextField(podForm.controls[ConfigurableField.LAND_REGISTRY_PARTICLE]?.value);
    let subdivision = mkTextField(podForm.controls[ConfigurableField.LAND_REGISTRY_SUBDIVISION]?.value);

    if (podAndLrdMap.has(pod)) {
        // pod had forbidden lrd, check for correction
        const forbiddenLrd = podAndLrdMap.get(pod);
        section = section ?? forbiddenLrd?.section;
        sheet = sheet ?? forbiddenLrd?.sheet;
        particle = particle ?? forbiddenLrd?.particle;
        subdivision = subdivision ?? forbiddenLrd?.subdivision;
    }

    return !!section || !!sheet || !!particle || !!subdivision ?
        {section, sheet, particle, subdivision} :
        null;
}

/**
 * createEcMemberPod for existent resources
 */
export function createEcMemberPod(podForm: FormGroup,
                                  energyCommunityNamedPod: EnergyCommunityNamedPod,
                                  currentAddress: ECMemberAddress,
                                  currentCoordinates: SpatialCoordinates,
                                  podAndLrdMap: Map<string, ECLandRegistryData>): ECMemberPOD {
    const podAddressLikeMember = podForm.controls.podAddressLikeMember.value as boolean;
    const podAddress = podAddressLikeMember ? currentAddress : podForm.controls.internalPodAddress.value;
    const podCoordinates = podAddressLikeMember ? currentCoordinates : podForm.controls.internalPodCoordinates.value;
    const creationType = podForm.controls.creationType.value || ECPodCreationType.MANUAL;
    const aspiringProsumer = podForm.controls.aspiringProsumer.value;
    return {
        pod: podForm.controls.podCode.value,
        pod_utility_type: podForm.controls.podUtilityType.value, // ECPodUtilityType; ?
        address: podAddress, // ECMemberAddress; ?
        has_wifi: podForm.controls.hasWifi.value, // boolean;
        seu: podForm.controls.seu.value, // boolean
        energy_provider: podForm.controls.podProvider.value, // string; ?
        land_registry_data: mkPodLandRegistryData(podForm, podAndLrdMap),
        spatial_coordinates: podCoordinates, // SpatialCoordinates; ?
        resource: energyCommunityNamedPod,
        creation_type: creationType,
        aspiring_prosumer: aspiringProsumer,
        substation: podForm.controls.substationCode.value
    };
}

/**
 * Member form validator of {@link NewECMemberComponent new ec member component}.
 */
export function validateMemberForm(ctrl: AbstractControl): ValidationErrors | null {
    const errs: ValidationErrors = {};
    const ctrls: { [ctrl: string]: AbstractControl } = (ctrl as FormGroup).controls;

    // validate legal form
    if (ctrls.juridicalType.value === ECJuridicalType.INDIVIDUAL && !ctrls.legalForm.value) {
        ctrls.legalForm.setErrors({[FormError.EMPTY]: true});
    } else {
        ctrls.legalForm.setErrors(null);
    }

    // validate subjectType
    if (ctrls.juridicalType.value === ECJuridicalType.CORPORATE_ENTITY &&
        (!ctrls.subjectType.value)) {
        ctrls.subjectType.setErrors({[FormError.EMPTY]: true});  // there is no actual visibility of these errors aside from the asterisks (field mandatory)
    } else {
        ctrls.subjectType.setErrors(null);
    }

    // validate confirm email
    if (ctrls[ConfigurableField.EMAIL] && ctrls.confirmEmail &&
        mkTextField(ctrls[ConfigurableField.EMAIL].value) !== mkTextField(ctrls.confirmEmail.value)) {
        ctrls.confirmEmail.setErrors({[FormError.NOT_MATCHING_EMAILS]: true});
    } else {
        ctrls.confirmEmail?.setErrors(null);
    }

    return Object.values(errs).every(err => !err) ? null : errs;
}

export function validatePOD(ctrl: AbstractControl): ValidationErrors {
    const v = ctrl.value;
    if (typeof v !== 'string' || isBlank(v)) { // allow optional control
        return null;
    }
    if (!smagValidatePod(v)) {
        return {[FormError.INVALID_POD_CODE]: true};
    }
    return null;
}

export function mkMemberForConfig(ecMember: ECMember, juridicalType: ECJuridicalType, legalFormType: ECLegalForm, subjectType: ECSubjectType): MemberForConfig {
    return {
        juridical_type: ecMember?.juridical_type ?? juridicalType,
        legal_form: ecMember?.legal_form ?? legalFormType,
        subject_type: ecMember?.subject_type ?? subjectType
    };
}

/**
 * Make a field config by checking if conditions of the deep config of form field config are matched.
 */
export function getMatchedConditions(configs: DeepFieldConfig[], member: MemberForConfig): FieldConfig[] {
    return configs
        .filter(config => Object.entries(config.condition).every(([key, value]) => member[key] === value))
        .map(({condition, ...overridingFieldConfig}) => overridingFieldConfig);
}

export function validateIfMandatory(fieldConfigs: Map<ConfigurableField, FieldConfig>, field: ConfigurableField): ValidatorFn {
    return fieldConfigs?.get(field)?.mandatory ? Validators.required : (() => null);
}

export function isFieldEnabled(fieldConfigs: Map<ConfigurableField, FieldConfig>, field: ConfigurableField): boolean {
    return !!fieldConfigs?.get(field)?.enabled;
}

/**
 * Some configurable fields must be added to or removed from the form dynamically, depending on the context and the config.
 */
export function setConfigurableDynamicControl(form: FormGroup,
                                              fieldConfigs: Map<ConfigurableField, FieldConfig>,
                                              field: ConfigurableField,
                                              validators: ValidatorFn[]) {
    const ctrl = form.controls[field];
    const isEnabled = isFieldEnabled(fieldConfigs, field);
    if (isEnabled) {
        if (!ctrl) {
            form.addControl(field, new FormControl(null, validators));
        } else {
            ctrl.clearValidators();
            ctrl.setErrors(null);
            ctrl.setValidators(validators);
            ctrl.setValue(ctrl.value); // trigger validation
        }
    } else if (!isEnabled && ctrl) {
        form.removeControl(field);
    }
}

/**
 * Add enabled configurable fields to the form with basic mandatory validation
 */
export function addConfigurableControls(fields: ConfigurableField[], fieldConfigs: Map<ConfigurableField, FieldConfig>, form: FormGroup) {
    fields
        .filter(field => isFieldEnabled(fieldConfigs, field))
        .forEach(field => form.addControl(field, new FormControl(null, [validateIfMandatory(fieldConfigs, field)])));
}

/**
 * Add enabled configurable fields to the form with basic mandatory validation and custom validators
 */
export function addConfigurableControlsWithAdditionalValidator(fields: [ConfigurableField, ValidatorFn][],
                                                               fieldConfigs: Map<ConfigurableField, FieldConfig>,
                                                               form: FormGroup) {
    fields
        .filter(([field, ignore]) => isFieldEnabled(fieldConfigs, field))
        .forEach(([field, additionalValidator]) =>
            form.addControl(field, new FormControl(null, [validateIfMandatory(fieldConfigs, field), additionalValidator])));
}

/**
 * Set enabled configurable form fields values
 */
export function setConfigurableControlsValues(fieldsAndValues: [ConfigurableField, unknown][],
                                              fieldConfigs: Map<ConfigurableField, FieldConfig>,
                                              form: FormGroup) {
    fieldsAndValues.filter(([field, ignore]) => isFieldEnabled(fieldConfigs, field))
        .forEach(([field, value]) => form.controls[field]?.setValue(value));
}

export function getDefaultEnergyProfile(isThirdPartyProducer: boolean, hasSEU: boolean) {
    if (isThirdPartyProducer && hasSEU) {
        return PodEnergyProfiles.PROSUMER;
    }
    if (isThirdPartyProducer) {
        return PodEnergyProfiles.PRODUCER;
    }
    return PodEnergyProfiles.CONSUMER;
}

export function rangeTaken(relations: ECMemberIdRelation[],
                           range: MemberRange,
                           timeZone: string, allowFutureMemberPodRelations: boolean): ECMemberIdRelation | null {
    // no role can be taken if there are no relations
    if (!relations.length) {
        return null;
    }
    const forbiddenRanges: MemberRange[] = relations.map(relation => [relation.start_date, relation.end_date]);
    if (isMemberPodRelationRangeValid(range, forbiddenRanges, timeZone, allowFutureMemberPodRelations)) {
        return null;
    }
    // find member of overlapping relation
    return relations.find(relation => rangeOverlaps(range, [relation.start_date, relation.end_date]));
}

function isMemberPodRelationRangeValid(range: MemberRange,
                                       forbiddenRanges: MemberRange[],
                                       timeZone: string,
                                       allowFutureMemberPodRelations: boolean): boolean {
    const today = moment.tz(timeZone).startOf('day').valueOf();
    if (!allowFutureMemberPodRelations && (range[0] > today || range[1] > today)) { // future start date not permitted
        return false;
    }
    return isRangeValid(forbiddenRanges, range);
}

export function mkPropertyUnitRelations(propertyUnitControls: FormGroup[]): ECMemberRelation[] {
    return propertyUnitControls.map(puForm =>
        mkPropertyUnitRelation(puForm.controls.id.value, puForm.controls.startDate.value, puForm.controls.endDate.value));
}

export function mkPodRelations(podControls: FormGroup[]): ECMemberRelation[] {
    return podControls.map(podForm =>
        mkMemberPodRelation(podForm.controls.podCode.value, podForm.controls.startDate.value, podForm.controls.endDate.value));
}

export function mkEnergyCommunityNamedPlant(podForm: FormGroup,
                                            formPlant: FormGroup,
                                            isThirdPartyProducer: boolean,
                                            timeZone: string): EnergyCommunityNamedPlant {
    return {
        id: null,
        name: formPlant.controls.plantName.value,
        plant: {
            type: EntityType.PLANT,
            sub_type: getEntitySubtype(formPlant.controls.plantType.value),
            voltage: podForm.controls.podVoltage.value,
            phases: podForm.controls.podPhase.value,
            nominal_power: formPlant.controls.nominalPower.value,
            activation_date: formPlant.controls.enterData.value ? formPlant.controls.enterData.value.valueOf() : null,
            incentive_mode: formPlant.controls.incentiveType.value,
            statistical_profile: formPlant.controls.profile.value,
            production_pod: !isBlank(formPlant.controls.podCode.value) ? formPlant.controls.podCode.value : null,
            predictive_models: [formPlant.controls.predictiveModel.value],
            capital_grant:
                isThirdPartyProducer ||
                isDefined(formPlant.controls.enterData.value) &&
                (formPlant.controls.enterData.value as Moment).isBefore(moment.tz(UPPER_INCENTIVE_BOUNDARY, timeZone)) ?
                    0.0 :
                    formPlant.controls.capitalGrant.value / 100
        }
    };
}

export function mkNewPod(podForm: FormGroup,
                         profiles: { predictiveModel?: PredictiveModelInfo, profile?: PodStatisticalProfile } | void,
                         podCode: string): Pod {
    return {
        type: EntityType.POD,
        voltage: podForm.controls.podVoltage.value,
        phases: podForm.controls.podPhase.value,
        producer: podForm.controls.podEnergyProfile.value === PodEnergyProfiles.PRODUCER,
        contractual_power: podForm.controls.contractualPower ? podForm.controls.contractualPower.value : null,
        predictive_models: !!profiles && !!profiles.predictiveModel ? [profiles.predictiveModel] : null,
        statistical_profile: !!profiles && !!profiles.profile ? profiles.profile : null,
        metadata: {
            [ENTITY_METADATA.POD_KEY]: podCode
        }
    };
}

export function mkEnergyCommunityNamedPod(podForm: FormGroup, pod: Pod, plants: EnergyCommunityNamedPlant[]): EnergyCommunityNamedPod {
    return {
        id: null,
        name: podForm.controls.podCode.value,
        pod,
        plants
    };
}

export function isPersistentPropertyUnit(propertyUnitForm: FormGroup): boolean {
    return !!propertyUnitForm.controls.persistentPropertyUnit.value as boolean;
}

export function isPersistentPod(podForm: FormGroup): boolean {
    return !!podForm.controls.persistentPod.value as boolean;
}

export function podExists(podForm: FormGroup): boolean {
    return podForm.controls.podExists.value as boolean;
}

export function plantsFormArray(podForm: FormGroup): FormArray {
    return podForm.controls.plants as FormArray;
}

export function plantControls(podForm: FormGroup): FormGroup[] {
    return plantsFormArray(podForm).controls as FormGroup[];
}

export function resetPodStatisticalProfile(podForm: FormGroup): void {
    podForm.controls.profile.setValue(null);
}

export function mkPropertyUnitTmpId(): string {
    // pattern is: ^\$[A-Za-z0-9_]+$
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789_';
    const length = 10;
    let result = '$';

    for (let i = 0; i < length; i++) {
        result += chars[Math.floor(Math.random() * chars.length)];
    }

    return result;
}

export function normalizeRange(range: MemberRange): MemberRange {
    const MIN = 0;
    const MAX = Number.POSITIVE_INFINITY;

    const start = !range[0] ? MIN : range[0];
    const end = !range[1] ? MAX : range[1];

    return [start, end];
}

function rangeOverlaps(rangeToCheck: MemberRange, rangeToAvoid: MemberRange): boolean {
    rangeToCheck = normalizeRange(rangeToCheck);
    rangeToAvoid = normalizeRange(rangeToAvoid);
    return rangeToCheck[0] <= rangeToAvoid[1] && rangeToCheck[1] >= rangeToAvoid[0];
}

export function isRangeValid(forbiddenRanges: MemberRange[], range: MemberRange): boolean {
    return !forbiddenRanges.some(fr => rangeOverlaps(range, fr));
}

export function getIndexOfOverlappingRange(forbiddenRanges: MemberRange[], range: MemberRange): number {
    return forbiddenRanges.findIndex(fr => rangeOverlaps(range, fr));
}

export function dateOrderValidator(): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
        const start = group.get('startDate')?.value;
        const end = group.get('endDate')?.value;
        if (!start || !end) {
            return null;
        }
        const startDate = moment(start);
        const endDate = moment(end);
        if (!startDate.isValid() || !endDate.isValid()) {
            return null;
        }
        return startDate.isSameOrBefore(endDate) ? null : {[PropertyUnitFormError.INVALID_RANGE]: true};
    };
}

export function mkPropertyUnitTriplet(propertyUnitForm: FormGroup): PropertyUnitTriplet {
    return {
        sheet: propertyUnitForm.controls.sheet.value,
        particle: propertyUnitForm.controls.particle.value,
        subdivision: propertyUnitForm.controls.subdivision.value
    };
}

export function isLandRegistryDataValued(propertyUnitForm: FormGroup): boolean {
    return !isBlank(propertyUnitForm.controls.sheet.value) &&
        !isBlank(propertyUnitForm.controls.particle.value) &&
        !isBlank(propertyUnitForm.controls.subdivision.value);
}

export function mkPropertyUnitKey(propertyUnitForm: FormGroup) {
    return `${propertyUnitForm.controls.sheet.value}/${propertyUnitForm.controls.particle.value}/${propertyUnitForm.controls.subdivision.value}`;
}

function setSeuIfPodExists(podForm: FormGroup, profile: PodEnergyProfiles) {
    if (profile === PodEnergyProfiles.PRODUCER || profile === PodEnergyProfiles.CONSUMER) {
        podForm.controls.seu.setValue(false);
        podForm.controls.seu.disable();
    } else if (profile === PodEnergyProfiles.PROSUMER) {
        podForm.controls.seu.setValue(true);
        podForm.controls.seu.disable();
    }
}

function isPodLocalDuplicateForCreate(podsFormArray: FormArray, i18n: I18NService) {
    // when a member is created, pod codes must not be repeated regardless of dates
    if (!podsFormArray) {
        return false;
    }
    const podsContainer = new Set<string>();
    for (const podControl of podsFormArray.controls) {
        const code = podControl.get('podCode')?.value;
        if (podsContainer.has(code)) {
            podControl.get('podCode')?.setErrors({[FormError.LOCAL_DUPLICATE_POD]: true});
            podControl.get('podErrorMessage')?.setValue(i18n.translate('Duplicated POD in member'));
            return true;
        } else if (podControl.get('podCode')?.hasError(FormError.LOCAL_DUPLICATE_POD)) {
            const {[FormError.LOCAL_DUPLICATE_POD]: removed, ...others} = podControl.get('podCode')?.errors || {};
            podControl.get('podCode')?.setErrors(Object.keys(others).length ? others : null);
        }
        podsContainer.add(code);
    }
    return false; // all pods are unique
}

function isPodLocalDuplicateForUpdate(podsFormArray: FormArray, i18n: I18NService) {
    if (!podsFormArray) {
        return false;
    }
    const podsContainer = new Set<string>();
    const forbiddenRanges = new Map<string, MemberRange[]>();
    for (const podControl of podsFormArray.controls) {
        const code = podControl.get('podCode')?.value;
        const startDate = podControl.get('startDate')?.value?.valueOf() ?? null;
        const endDate = podControl.get('endDate')?.value?.valueOf() ?? null;
        const range = [startDate, endDate] as MemberRange;
        if (podsContainer.has(code) && !isRangeValid(forbiddenRanges.get(code) ?? [], range)) {
            podControl.get('podCode')?.setErrors({[FormError.LOCAL_DUPLICATE_POD]: true});
            podControl.get('podErrorMessage')?.setValue(i18n.translate('Invalid selected period.'));
            return true;
        } else if (podControl.get('podCode')?.hasError(FormError.LOCAL_DUPLICATE_POD)) {
            const {[FormError.LOCAL_DUPLICATE_POD]: removed, ...others} = podControl.get('podCode')?.errors || {};
            podControl.get('podCode')?.setErrors(Object.keys(others).length ? others : null);
        }
        podsContainer.add(code);
        forbiddenRanges.set(code, [...(forbiddenRanges.get(code) ?? []), range]);
    }
    return false; // all pods are unique
}

export function validatePodControl(podInfo: PodInfo,
                                   podForm: FormGroup,
                                   podsFormArray: FormArray,
                                   isThirdPartyProducer: boolean,
                                   isMobileMember: boolean,
                                   i18n: I18NService,
                                   isUpdate: boolean) {
    const podCode = podForm.controls.podCode;
    const podInfoMessage = podForm.controls.podInfoMessage;
    const podErrorMessage = podForm.controls.podErrorMessage;

    if (!canValidatePod(podForm)) { // validate input only if dirty
        return;
    }

    // invalid pod code syntax already handled

    if (isUpdate ? isPodLocalDuplicateForUpdate(podsFormArray, i18n) : isPodLocalDuplicateForCreate(podsFormArray, i18n)) {
        // pod code error already set
        // pod error message already set
        return;
    }

    if (!podInfo) {
        // pod is brand new and is not present in the back-end, further validation is not needed
        return;
    }

    // if pod info is available, it means that POD already exists,
    // and it is a community pod or a member pod
    podForm.controls.podExists.setValue(true);

    if (isProducerResource(podForm) && isMobileMember) {
        // the following situation is not allowed:
        // - pod already exists
        // - pod is a producer
        // - member is mobile
        podErrorMessage.setValue(i18n.translate('Enter a new POD or consumer/prosumer POD.'));
        podCode.setErrors({[FormError.POD]: true});
        return;
    }

    if (isThirdPartyProducer && (isConsumerResource(podForm) || isProsumerResource(podForm) && !isSeu(podForm))) {
        // third party producer can't have a consumer resource or, if seu is disabled, a prosumer resource
        podErrorMessage.setValue(i18n.translate('Enter a new POD or producer POD.'));
        podCode.setErrors({[FormError.POD]: true});
        return;
    }

    if (isCommunityPod(podInfo)) {
        // set info message to show later
        podInfoMessage.setValue(i18n.translate('POD matches resource {0}.', podInfo.podResource.name));
    } else { // is member pod
        // only expose "taken" ranges (i.e. skip current member, in case)
        if (!podInfo.membersInfo.rangeTakenBy) {
            // duplicate pod without range conflict
            podInfoMessage.setValue(i18n.translate('This POD has a holder.'));
        } else if (podInfo.membersInfo.rangeTakenBy.memberName) {
            // duplicate pod with range conflict and member info
            podCode.setErrors({[FormError.POD]: true});
            podErrorMessage.setValue(i18n.translate('{0} is holder of this POD for the selected period. Please change the selected period.',
                podInfo.membersInfo.rangeTakenBy.memberName));
        } else {
            // duplicate pod with range conflict but without member info
            // member name is not available (e.g. due to lack of permissions)
            podCode.setErrors({[FormError.POD]: true});
            podErrorMessage.setValue(i18n.translate('This POD has a holder for the selected period. Please change the selected period.'));
        }
    }
    if (podInfo.podResource) {
        // pod resource info are available
        // check if pod can have capital grant
        if (isThirdPartyProducer && (podInfo.podResource.plants || []).some(ecPlant => ecPlant.plant.capital_grant > 0)) {
            podErrorMessage.setValue(i18n.translate('Enter a new POD or producer POD without a configured capital grant.'));
            podCode.setErrors({[FormError.POD]: true});
            return;
        }
        // set energy profile, pod name and seu
        const profile = getPodEnergyProfile(podInfo.podResource);
        podForm.controls.podEntityEnergyProfile.setValue(profile);
        podForm.controls.podName.setValue(podInfo.podResource.name);
        setSeuIfPodExists(podForm, profile);
    } else {
        // at this point, some resource MUST actually correspond to this POD;
        // thus, if no such data are available, it must be due to a lack of permissions
        // (or POD belongs to another community);
        // this is currently "masked" under a generic uniqueness error
        podCode.setErrors({[FormError.DUPLICATE_POD]: true});
    }
}

export function handlePropertyUnitValidation(puWithRelations: ECMemberPropertyUnitWithRelations,
                                             propertyUnitForm: FormGroup,
                                             i18n: I18NService,
                                             datesMap: Map<string, MemberRange>) {
    const errorMessageControl = propertyUnitForm.get('errorMessage');
    const propertyUnitId = puWithRelations?.id ?? null;
    let forbiddenRanges: MemberRange[] = puWithRelations ? puWithRelations.relations.map(r => normalizeRange([r.start_date, r.end_date])) : [];
    const memberNames = puWithRelations ? puWithRelations.relations.map(r => r.member_name) : [];
    // if a property unit exists without an owner, then every land registry data field must match, even the optional one
    // when it happens, id is not null and relations is empty
    if (propertyUnitId != null &&
        puWithRelations?.relations.length === 0 &&
        mkTextField(propertyUnitForm.controls.sheet.value) === mkTextField(puWithRelations.sheet) &&
        mkTextField(propertyUnitForm.controls.particle.value) === mkTextField(puWithRelations.particle) &&
        mkTextField(propertyUnitForm.controls.subdivision.value) === mkTextField(puWithRelations.subdivision) &&
        mkTextField(propertyUnitForm.controls.section.value) !== mkTextField(puWithRelations.section)) {
        if (isDefined(puWithRelations.section)) {
            errorMessageControl.setValue(i18n.translate('A property unit with this land registry data is already existing. The section must match \'{0}\' or a new property unit must be entered.', puWithRelations.section));
        } else {
            errorMessageControl.setValue(i18n.translate('A property unit with this land registry data is already existing. Remove the section \'{0}\' or enter a new property unit.', propertyUnitForm.controls.section.value));
        }
        errorMessageControl.setErrors({[PropertyUnitFormError.DUPLICATE_PROPERTY_UNITS]: true});
        return;
    }
    // clear error message
    errorMessageControl.setValue(null);
    // clear errors
    const {[PropertyUnitFormError.DUPLICATE_PROPERTY_UNITS]: removed, ...others} = errorMessageControl.errors || {};
    errorMessageControl.setErrors(Object.keys(others).length ? others : null);

    // in case of update, self range overlap check must be avoided
    // the original value of the current edited range (previous range) must be excluded from the already existing ranges
    const previousRange = datesMap.get(propertyUnitForm.controls.formId.value);
    forbiddenRanges = forbiddenRanges.filter(fr => fr[0] !== previousRange?.[0] && fr[1] !== previousRange?.[1]);
    if (!(propertyUnitId && forbiddenRanges.length)) {
        // the property unit does not exist
        return;
    }
    // the property unit already exists
    // check validity by checking dates in relations
    // only one property unit for a given time range must exist
    const start = propertyUnitForm.controls.startDate.value?.valueOf();
    const end = propertyUnitForm.controls.endDate.value?.valueOf();
    const currentRange: MemberRange = [start, end];
    // if the current range is invalid then the current property unit is a duplicate
    const indexOfOverlappingRange = getIndexOfOverlappingRange(forbiddenRanges, currentRange);
    if (indexOfOverlappingRange !== -1) {
        // there is a range conflict
        const memberName = memberNames[indexOfOverlappingRange];
        if (memberName) { // show name if available
            errorMessageControl.setValue(i18n.translate('{0} is owner of this property unit for the selected period. Please change the selected period.', memberName));
        } else { // or show a generic error
            errorMessageControl.setValue(i18n.translate('This property unit has an owner for the selected period. Please change the selected period.'));
        }
        errorMessageControl.setErrors({[PropertyUnitFormError.DUPLICATE_PROPERTY_UNITS]: true});
        return;
    }
    // the property unit exists without range conflicts
    // replace current property unit temporary id with the existing one
    propertyUnitForm.controls.id.setValue(propertyUnitId);
    propertyUnitForm.controls.infoMessage.setValue(i18n.translate('This property unit has an owner.'));
    return;
}

export function isPropertyUnitLocalDuplicate(propertyUnitsFormArray: FormArray,
                                             i18n: I18NService) {
    // property units are unique on sheet, particle, subdivision and time range
    // the form must not have duplicates
    if (!propertyUnitsFormArray) {
        return false;
    }

    const propertyUnitsContainer = new Set<string>();
    const forbiddenRanges = [];
    for (const propertyUnitControl of propertyUnitsFormArray.controls) {
        const errorMessageControl = propertyUnitControl.get('errorMessage');
        // clear error message
        errorMessageControl.setValue(null);
        // clear errors
        const {[PropertyUnitFormError.LOCAL_DUPLICATE_PROPERTY_UNITS]: removed, ...others} = errorMessageControl.errors || {};
        errorMessageControl.setErrors(Object.keys(others).length ? others : null);

        const sheet = propertyUnitControl.get('sheet')?.value;
        const particle = propertyUnitControl.get('particle')?.value;
        const subdivision = propertyUnitControl.get('subdivision')?.value;
        const startDate = propertyUnitControl.get('startDate')?.value;
        const endDate = propertyUnitControl.get('endDate')?.value;
        const range = [startDate, endDate] as MemberRange;
        const key = `${sheet}/${particle}/${subdivision}`;
        if (propertyUnitsContainer.has(key) && !isRangeValid(forbiddenRanges, range)) {
            errorMessageControl.setValue(i18n.translate('Invalid selected period. Please change the selected period.'));
            errorMessageControl.setErrors({[PropertyUnitFormError.LOCAL_DUPLICATE_PROPERTY_UNITS]: true});
            return true;
        }
        propertyUnitsContainer.add(key);
        forbiddenRanges.push(range);
    }
    return false;
}


// pod validation --------------------------------------------------------------------------------------------------------------------------
function canValidatePod(podForm: FormGroup) {
    return podForm.controls.podCode.dirty;
}

/**
 * pod code syntax is invalid ({@link validatePOD}) or input is empty ({@link validateNotBlank})
 */
export function podIsInvalid(podForm: FormGroup, i18n: I18NService) {
    const podCodeControl = podForm.controls.podCode;
    if (podCodeControl.hasError(FormError.INVALID_POD_CODE) || podCodeControl.hasError(FormError.EMPTY)) {
        podForm.controls.podErrorMessage.setValue(i18n.translate('Invalid POD.'));
        return true;
    }
    return false;
}

export function plantPodError(fg: FormGroup): FormError | null {
    const podCodeControl = fg.controls.podCode;
    if (podCodeControl.dirty) {
        if (podCodeControl.hasError(FormError.POD) || podCodeControl.hasError(FormError.EMPTY)) {
            return FormError.POD;
        }
        if (podCodeControl.hasError(FormError.UNIQUE_POD)) {
            return FormError.UNIQUE_POD;
        }
    }
    return null;
}


function isCommunityPod(info: PodInfo): boolean {
    return info.podResource && !info.membersInfo?.members?.length;
}

export function isProducerResource(podForm: FormGroup) {
    return podForm.controls.podEntityEnergyProfile.value as PodEnergyProfiles === PodEnergyProfiles.PRODUCER;
}

export function isProsumerResource(podForm: FormGroup) {
    return podForm.controls.podEntityEnergyProfile.value as PodEnergyProfiles === PodEnergyProfiles.PROSUMER;
}

export function isConsumerResource(podForm: FormGroup) {
    return podForm.controls.podEntityEnergyProfile.value as PodEnergyProfiles === PodEnergyProfiles.CONSUMER;
}

export function isSeu(podForm: FormGroup) {
    return podForm.controls.seu.value === true;
}

function isLandRegistryDataForbidden(data: string) {
    // hide auto generated values ('$$...$$')
    return !isBlank(data) && data.length > 4 && (data.slice(0, 2) === '$$' || data.slice(-2) === '$$');
}

export function isPropertyUnitForbidden(propertyUnit: ECMemberPropertyUnit | ECEconomicsRedistributedNewCondoRow | ECLandRegistryData) {
    // hide property unit with only auto generated values ('$$...$$') as land registry data
    return isLandRegistryDataForbidden(propertyUnit.section) || isLandRegistryDataForbidden(propertyUnit.sheet) ||
        isLandRegistryDataForbidden(propertyUnit.particle) || isLandRegistryDataForbidden(propertyUnit.subdivision);
}
